// QuickBooks Online -- matching a Stede vendor to a QuickBooks supplier.
//
// Session 78n9.2, Issue #298 / part of #395.
// Gate: docs/gates/Stede_DocumentOutput_Gate_395.html, Screen 4, callout N.
//
// WHAT THIS IS, in plain English: Stede calls a supplier "Houston Sign Supply".
// Bridgette's QuickBooks might call the same company "Houston Sign Supply,
// Inc." This suggests which QuickBooks supplier a Stede vendor probably is, so
// that linking them is a confirmation rather than a search.
//
// THE POINT OF CALLOUT N, and it governs everything here: the match runs ONCE
// and is then remembered by ID, in inventory_vendors.quickbooks_vendor_id.
// After that, either system can rename the supplier freely and the link still
// holds. Name matching is a convenience at the moment of linking and is never
// how a bill finds its supplier -- the file import's exact-name requirement is
// the constraint this design deliberately does not inherit.
//
// So the bar for suggesting is low and the bar for AUTO-LINKING is high: a
// wrong auto-link posts a bill to the wrong company's account, and nobody
// re-reads a link that appeared by itself.

// Legal-form suffixes and joining words that differ between two systems far
// more often than the company does. Stripped for comparison only -- the name a
// human sees is never rewritten.
const NOISE = new Set([
  'inc', 'incorporated', 'llc', 'llp', 'lp', 'ltd', 'limited', 'co', 'corp',
  'corporation', 'company', 'the', 'and', 'of',
]);

// Normalise a supplier name for comparison. Punctuation goes, case goes,
// ampersands become "and" before the noise pass so "Smith & Sons" and
// "Smith and Sons" agree, and repeated whitespace collapses.
export function normalizeVendorName(raw) {
  if (raw == null) return '';
  return String(raw)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !NOISE.has(w))
    .join(' ')
    .trim();
}

export function vendorTokens(raw) {
  const n = normalizeVendorName(raw);
  return n ? n.split(' ') : [];
}

// QuickBooks suppliers whose names normalise to the same thing. This is the
// half of callout N that is about Bridgette's data rather than Stede's: her
// list carries its own duplicates, and the moment somebody is choosing between
// two identical-looking rows is the moment to say so.
export function duplicateSuppliers(qbVendors) {
  const byKey = new Map();
  for (const v of qbVendors || []) {
    const key = normalizeVendorName(v?.name);
    if (!key) continue;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(v);
  }
  const out = [];
  for (const [key, group] of byKey) {
    if (group.length > 1) out.push({ key, vendors: group });
  }
  return out;
}

// How alike two normalised names are, as a share of the smaller token set.
// Deliberately simple: this ranks candidates for a human to look at, it does
// not decide anything on its own.
function tokenOverlap(a, b) {
  if (a.length === 0 || b.length === 0) return 0;
  const setB = new Set(b);
  const shared = a.filter((t) => setB.has(t)).length;
  return shared / Math.min(a.length, b.length);
}

export const MATCH = {
  EXACT: 'exact',
  LIKELY: 'likely',
  POSSIBLE: 'possible',
  NONE: 'none',
};

/**
 * Suggest which QuickBooks supplier a Stede vendor is.
 *
 * stedeVendor: { id, name }
 * qbVendors:   [{ id, name, companyName }]
 *
 * Returns { confidence, autoLinkable, best, candidates, ambiguous }.
 *
 * `autoLinkable` is true ONLY when exactly one supplier matches exactly after
 * normalisation. Two suppliers that both match exactly is the ambiguous case,
 * and it is reported rather than resolved -- picking the first would be a coin
 * toss dressed up as an answer.
 */
export function suggestVendorMatch(stedeVendor, qbVendors) {
  const target = normalizeVendorName(stedeVendor?.name);
  const targetTokens = vendorTokens(stedeVendor?.name);
  const list = Array.isArray(qbVendors) ? qbVendors : [];

  if (!target || list.length === 0) {
    return { confidence: MATCH.NONE, autoLinkable: false, best: null, candidates: [], ambiguous: false };
  }

  const scored = list
    .map((v) => {
      const norm = normalizeVendorName(v?.name);
      const alt = normalizeVendorName(v?.companyName);
      const exact = norm === target || (!!alt && alt === target);
      const overlap = Math.max(
        tokenOverlap(targetTokens, vendorTokens(v?.name)),
        alt ? tokenOverlap(targetTokens, vendorTokens(v?.companyName)) : 0,
      );
      return { vendor: v, exact, score: exact ? 1 : overlap };
    })
    .filter((s) => s.exact || s.score >= 0.5)
    .sort((a, b) => b.score - a.score);

  const exacts = scored.filter((s) => s.exact);

  if (exacts.length === 1) {
    return {
      confidence: MATCH.EXACT,
      autoLinkable: true,
      best: exacts[0].vendor,
      candidates: scored.map((s) => s.vendor),
      ambiguous: false,
    };
  }

  if (exacts.length > 1) {
    // Two QuickBooks suppliers with the same name. Bridgette's duplicate, and
    // the one case where doing nothing is the correct behaviour.
    return {
      confidence: MATCH.EXACT,
      autoLinkable: false,
      best: null,
      candidates: exacts.map((s) => s.vendor),
      ambiguous: true,
    };
  }

  if (scored.length === 0) {
    return { confidence: MATCH.NONE, autoLinkable: false, best: null, candidates: [], ambiguous: false };
  }

  const top = scored[0];
  return {
    confidence: top.score >= 0.8 ? MATCH.LIKELY : MATCH.POSSIBLE,
    autoLinkable: false,
    best: top.vendor,
    candidates: scored.map((s) => s.vendor),
    ambiguous: false,
  };
}

/**
 * Run the suggestion across a whole vendor list, which is what the mapping
 * screen opens with.
 *
 * Returns { linked, auto, review, unmatched } -- four buckets, because the
 * screen's job is to show a human the ones that need them, not a list of
 * everything.
 */
export function matchAllVendors(stedeVendors, qbVendors) {
  const linked = [];
  const auto = [];
  const review = [];
  const unmatched = [];

  for (const v of stedeVendors || []) {
    if (v?.quickbooks_vendor_id) {
      linked.push({ vendor: v });
      continue;
    }
    const s = suggestVendorMatch(v, qbVendors);
    if (s.autoLinkable) auto.push({ vendor: v, suggestion: s });
    else if (s.candidates.length > 0) review.push({ vendor: v, suggestion: s });
    else unmatched.push({ vendor: v, suggestion: s });
  }

  return { linked, auto, review, unmatched };
}
